import { useOuterClick } from '@helpers/useOuterClick';
import { Tooltip } from '@components/ui/Tooltip';
import { useTranslation } from 'react-i18next';
import * as Shared from '@shared/lib';
import { useState } from 'react';

interface AbilityProps {
  health: number;
  energy: number;
  ability: Shared.AbilityData;
  cooldowns: Shared.AbilitySync[];
  onClick: (ability: Shared.AbilityData) => void;
}

export function Ability({ health, energy, ability, cooldowns, onClick }: AbilityProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipTimeout, setTooltipTimeout] = useState<Timer>();
  const [tooltipStarted, setTooltipStarted] = useState(0);
  const abilityRef = useOuterClick(() => setShowTooltip(false));

  const { t } = useTranslation(undefined, { keyPrefix: 'Abilities' });

  const requiredEnergy = ability.energy ?? 0;
  const cooldown = cooldowns.find(c => c.code === ability.id)?.cooldown ?? 0;
  const disabled = energy < requiredEnergy || cooldown > 0 || health <= 0;
  const textClass = disabled ? 'text-gray-700' : 'active';

  const mouseDown = () => {
    setTooltipStarted(Date.now());
    setTooltipTimeout(setTimeout(() => {
      setShowTooltip(true);
    }, 500));
  };

  const click = () => {
    if (Date.now() - tooltipStarted < 500 && !disabled) {
      onClick(ability);
    }

    clearTimeout(tooltipTimeout);
    setShowTooltip(false);
    setTooltipStarted(0);
  };

  const TooltipOverlay = () => (
    <div className="p-2">
      <p className="text-base text-primary">{t(`${ability.key}.name`)}</p>
      <p className="mt-2 max-w-64">{t(`${ability.key}.description`)}</p>
      <p className="mt-2 text-primary">{t(`energyCost`)}: {ability.energy}</p>
    </div>
  );

  return (
    <Tooltip
      targetClassName="flex-1 text-center"
      overlay={<TooltipOverlay />}
      className="ability-tooltip"
      visible={showTooltip}
      placement="top-start"
    >
      <div className="select-none" ref={abilityRef}>
        <button className="relative mx-auto" onPointerDown={mouseDown} onClick={click}>
          <p className={`${textClass} text-ascentian text-2xl mt-[-2px]`}>
            {ability.ascentian}
          </p>

          <p className="text-center text-xs text-gray-500 mt-0.5">
            {t(`${ability.key}.shortInfo`)}
          </p>

          {cooldown > 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white text-xl text-mono font-bold">{cooldown}</span>
            </div>
          )}
        </button>
      </div>
    </Tooltip>
  );
}